import { z } from 'zod';

import { env } from '@/env';
import { graphql, type DocumentType } from '@/gql';

export const GET_COURSE_URL_COURSE_FRAGMENT = graphql(`
  fragment getCourseUrlCourseFields on GenericTrainingCourse {
    _id
    isOnline
    provider {
      _id
    }
    service {
      _id
      machineName
    }
    actionUrls {
      enrolOnCourse
    }
  }
`);

export type GetCourseUrlCourse = DocumentType<
  typeof GET_COURSE_URL_COURSE_FRAGMENT
>;

const TRAINING_COURSE_ID_CODEC = z.codec(
  z.string(),
  z.object({
    id: z.string().min(1),
  }),
  {
    decode: (courseId, ctx) => {
      try {
        return JSON.parse(atob(courseId));
      } catch (error) {
        ctx.issues.push({
          code: 'invalid_format',
          format: 'json',
          input: courseId,
          message: error instanceof Error ? error.message : 'Invalid course ID',
        });

        return z.NEVER;
      }
    },
    encode: (payload) => btoa(JSON.stringify(payload)),
  },
);

const getKnowledgeCourseId = (course: GetCourseUrlCourse) => {
  const { _id: courseId, isOnline, provider } = course;

  const isKnowledgeCourse =
    provider?._id === env.NEXT_PUBLIC_GOVERNORHUB_KNOWLEDGE_TRAINING_PROVIDER;

  if (!courseId || !isOnline || !isKnowledgeCourse) {
    return null;
  }

  const { success, data } = TRAINING_COURSE_ID_CODEC.safeDecode(courseId);

  if (!success) {
    return null;
  }

  return data.id;
};

export const getCourseUrl = (course: GetCourseUrlCourse | null | undefined) => {
  if (!course) {
    return null;
  }

  const { _id: courseId, isOnline, service, actionUrls } = course;
  const { machineName } = service ?? {};
  const { enrolOnCourse } = actionUrls ?? {};

  if (!courseId) {
    return null;
  }

  const knowledgeCourseId = getKnowledgeCourseId(course);

  if (knowledgeCourseId) {
    return {
      url:
        env.NEXT_PUBLIC_KSG_LEARNING_PATHWAY_REDIRECT_URL +
        encodeURIComponent(knowledgeCourseId),
      external: true,
      isKnowledgeCourse: true,
    };
  }

  if (isOnline && enrolOnCourse) {
    return { url: enrolOnCourse, external: true, isKnowledgeCourse: false };
  }

  if (!isOnline && machineName) {
    return {
      url: `/s/${encodeURIComponent(machineName)}/training?courseId=${courseId}`,
      external: false,
      isKnowledgeCourse: false,
    };
  }

  return null;
};

export const openCourseUrl = (
  course: GetCourseUrlCourse | null | undefined,
) => {
  const courseLink = getCourseUrl(course);

  if (!courseLink?.external) {
    return false;
  }

  const windowFeatures = courseLink.isKnowledgeCourse
    ? 'noopener'
    : 'noopener,noreferrer';

  window.open(courseLink.url, '_blank', windowFeatures);

  return true;
};
